// Migration utility for old-shape pending teachers
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  updateDoc, 
  doc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

export const migratePendingTeachers = async () => {
  console.log('🔧 Starting migration of old-shape pending teachers...');
  
  try {
    // Query for old-shape pending teachers: status=pending && role=teacher
    const usersRef = collection(db, "users");
    const q = query(
      usersRef, 
      where("status", "==", "pending"), 
      where("role", "==", "teacher")
    );
    
    const snap = await getDocs(q);
    
    console.log(`📊 Found ${snap.docs.length} old-shape pending teachers to migrate`);
    
    const results = {
      total: snap.docs.length,
      migrated: 0,
      errors: [],
      details: []
    };
    
    // Migrate each old-shape pending teacher
    for (const docSnap of snap.docs) {
      try {
        const userData = docSnap.data();
        
        console.log(`🔄 Migrating user: ${userData.email} (${docSnap.id})`);
        
        // Update to new shape: requestedRole=teacher, role=null
        await updateDoc(doc(db, "users", docSnap.id), {
          requestedRole: "teacher",  // New standardized field
          role: null,                // Clear the old role field
          migratedAt: serverTimestamp(),  // Track when migration happened
          migrationNote: "Migrated from old-shape pending teacher to new pending approval system"
        });
        
        results.migrated++;
        results.details.push({
          id: docSnap.id,
          email: userData.email,
          status: 'success',
          oldShape: { role: 'teacher', status: 'pending' },
          newShape: { requestedRole: 'teacher', role: null, status: 'pending' }
        });
        
        console.log(`✅ Successfully migrated: ${userData.email}`);
        
      } catch (error) {
        console.error(`❌ Error migrating user ${docSnap.id}:`, error);
        results.errors.push({
          id: docSnap.id,
          email: docSnap.data().email,
          error: error.message
        });
      }
    }
    
    console.log(`🎉 Migration completed! ${results.migrated}/${results.total} users migrated successfully`);
    
    if (results.errors.length > 0) {
      console.warn(`⚠️ ${results.errors.length} errors occurred during migration`);
      results.errors.forEach(error => {
        console.warn(`- ${error.email}: ${error.error}`);
      });
    }
    
    return results;
    
  } catch (error) {
    console.error('💥 Migration failed with critical error:', error);
    throw error;
  }
};

export const dryRunPendingTeachersMigration = async () => {
  console.log('🧪 Running dry-run for pending teachers migration...');
  
  try {
    // Query for old-shape pending teachers: status=pending && role=teacher
    const usersRef = collection(db, "users");
    const q = query(
      usersRef, 
      where("status", "==", "pending"), 
      where("role", "==", "teacher")
    );
    
    const snap = await getDocs(q);
    
    console.log(`📊 DRY RUN: Would migrate ${snap.docs.length} old-shape pending teachers`);
    
    const results = {
      wouldMigrate: snap.docs.length,
      details: []
    };
    
    snap.docs.forEach(docSnap => {
      const userData = docSnap.data();
      results.details.push({
        id: docSnap.id,
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        currentShape: {
          role: userData.role,
          requestedRole: userData.requestedRole,
          status: userData.status
        },
        proposedChanges: {
          role: null,
          requestedRole: "teacher",
          status: "pending"  // Keep status as pending
        },
        action: 'MIGRATE: role="teacher" → requestedRole="teacher", role=null'
      });
    });
    
    console.log('🧪 Dry run completed successfully');
    return results;
    
  } catch (error) {
    console.error('💥 Dry run failed:', error);
    throw error;
  }
};

export const checkPendingTeachersStatus = async () => {
  console.log('🔍 Checking status of pending teachers...');
  
  try {
    const usersRef = collection(db, "users");
    
    // Count old-shape pending teachers
    const qOld = query(
      usersRef, 
      where("status", "==", "pending"), 
      where("role", "==", "teacher")
    );
    
    // Count new-shape pending teachers
    const qNew = query(
      usersRef,
      where("status", "==", "pending"),
      where("requestedRole", "==", "teacher")
    );
    
    const [oldSnap, newSnap] = await Promise.all([
      getDocs(qOld),
      getDocs(qNew)
    ]);
    
    const results = {
      oldShapePendingTeachers: oldSnap.docs.length,
      newShapePendingTeachers: newSnap.docs.length,
      totalPendingTeachers: oldSnap.docs.length + newSnap.docs.length,
      migrationNeeded: oldSnap.docs.length > 0,
      oldShapeDetails: oldSnap.docs.map(doc => ({
        id: doc.id,
        email: doc.data().email,
        name: `${doc.data().firstName || ''} ${doc.data().lastName || ''}`.trim(),
        createdAt: doc.data().createdAt
      })),
      newShapeDetails: newSnap.docs.map(doc => ({
        id: doc.id,
        email: doc.data().email,
        name: `${doc.data().firstName || ''} ${doc.data().lastName || ''}`.trim(),
        createdAt: doc.data().createdAt
      }))
    };
    
    console.log(`📊 Status check complete:
    - Old-shape pending teachers: ${results.oldShapePendingTeachers}
    - New-shape pending teachers: ${results.newShapePendingTeachers}
    - Migration needed: ${results.migrationNeeded ? 'YES' : 'NO'}`);
    
    return results;
    
  } catch (error) {
    console.error('💥 Status check failed:', error);
    throw error;
  }
};